import axios from "axios";
import { logEvent, getDeviceId, newRequestId } from "./logger";
import { redactUrl, redactValue } from "./redact";

/**
 * Every request this device makes and every socket it opens.
 *
 * Each /api request carries two headers the server records against its own log row:
 *   X-Device-Id  — which phone
 *   X-Request-Id — which request, so a tap here can be matched to the response there
 *
 * Success bodies are not stored (size only) — a menu bootstrap is megabytes; error
 * bodies are, because they are what explains a failure.
 */
const LOG_UPLOAD_PATH = "/api/logs/ingest";
// Polled constantly; logged only when they fail.
const QUIET_PATHS = ["/api/health", "/api/realtime/health"];
const ERROR_BODY_LIMIT = 2000;
const HEARTBEAT_SUMMARY_MS = 5 * 60 * 1000;

let installed = false;

const pathOf = url => {
  try {
    return new URL(String(url), window.location.origin).pathname;
  } catch {
    return String(url || "");
  }
};

const isApi = url => pathOf(url).startsWith("/api/");
const isLogUpload = url => pathOf(url) === LOG_UPLOAD_PATH;
const isQuiet = url => QUIET_PATHS.includes(pathOf(url));

function bodyForLog(data) {
  if (data === undefined || data === null || data === "") return undefined;
  if (typeof FormData !== "undefined" && data instanceof FormData) return { form_data: true };
  if (typeof Blob !== "undefined" && data instanceof Blob) return { bytes: data.size };
  if (typeof data === "string") {
    try {
      return redactValue(JSON.parse(data));
    } catch {
      return redactValue(data.slice(0, ERROR_BODY_LIMIT));
    }
  }
  return redactValue(data);
}

function errorBody(data) {
  if (data === undefined || data === null) return undefined;
  const text = typeof data === "string" ? data : JSON.stringify(data);
  return bodyForLog(text.length > ERROR_BODY_LIMIT ? text.slice(0, ERROR_BODY_LIMIT) : data);
}

function setHeader(headers, name, value) {
  if (!headers) return;
  if (typeof headers.set === "function") headers.set(name, value);
  else headers[name] = value;
}

// ── axios ───────────────────────────────────────────────────────────────────────
export function instrumentAxios(instance) {
  if (!instance || instance.__vibezLogged) return instance;
  instance.__vibezLogged = true;

  instance.interceptors.request.use(config => {
    try {
      const url = `${config.baseURL || ""}${config.url || ""}`;
      if (!isApi(url) || isLogUpload(url)) return config;
      const requestId = newRequestId();
      config.headers = config.headers || {};
      setHeader(config.headers, "X-Request-Id", requestId);
      setHeader(config.headers, "X-Device-Id", getDeviceId());
      config.__vibezLog = { requestId, started: performance.now(), url, quiet: isQuiet(url) };
      if (!isQuiet(url)) {
        logEvent(
          "http_request",
          {
            method: String(config.method || "get").toUpperCase(),
            url: redactUrl(url),
            body: bodyForLog(config.data),
          },
          { requestId }
        );
      }
    } catch {
      /* never block a request over logging */
    }
    return config;
  });

  instance.interceptors.response.use(
    response => {
      try {
        const meta = response.config?.__vibezLog;
        if (meta && !meta.quiet) {
          logEvent(
            "http_response",
            {
              method: String(response.config.method || "get").toUpperCase(),
              url: redactUrl(meta.url),
              status: response.status,
              duration_ms: Math.round(performance.now() - meta.started),
              replayed: response.headers?.["x-idempotent-replayed"] || undefined,
            },
            { requestId: meta.requestId }
          );
        }
      } catch {
        /* the response still reaches its caller */
      }
      return response;
    },
    error => {
      try {
        const meta = error?.config?.__vibezLog;
        if (meta) {
          logEvent(
            "http_error",
            {
              method: String(error.config.method || "get").toUpperCase(),
              url: redactUrl(meta.url),
              status: error.response?.status ?? null,
              // ERR_NETWORK / ECONNABORTED etc. — the difference between "the server said no"
              // and "the request never arrived".
              code: error.code || undefined,
              message: String(error.message || "").slice(0, 300),
              duration_ms: Math.round(performance.now() - meta.started),
              response: errorBody(error.response?.data),
              online: typeof navigator !== "undefined" ? navigator.onLine : undefined,
            },
            { requestId: meta.requestId }
          );
        }
      } catch {
        /* the rejection still reaches its caller */
      }
      return Promise.reject(error);
    }
  );
  return instance;
}

// ── fetch ───────────────────────────────────────────────────────────────────────
function installFetch() {
  if (typeof window.fetch !== "function" || window.fetch.__vibezLogged) return;
  const native = window.fetch.bind(window);
  const logged = async (input, init = {}) => {
    const url = typeof input === "string" || input instanceof URL ? String(input) : input?.url;
    if (!isApi(url) || isLogUpload(url)) return native(input, init);
    const requestId = newRequestId();
    const method = String(init.method || input?.method || "GET").toUpperCase();
    const quiet = isQuiet(url);
    let request = input;
    let options = init;
    // Headers are only added when this code owns the request shape; a Request object
    // passed in keeps exactly the headers its caller built.
    if (typeof input === "string" || input instanceof URL) {
      const headers = new Headers(init.headers || {});
      headers.set("X-Request-Id", requestId);
      headers.set("X-Device-Id", getDeviceId());
      options = { ...init, headers };
    }
    const started = performance.now();
    if (!quiet) logEvent("http_request", { method, url: redactUrl(url), body: bodyForLog(init.body), via: "fetch" }, { requestId });
    try {
      const response = await native(request, options);
      if (!quiet || !response.ok) {
        logEvent(
          response.ok ? "http_response" : "http_error",
          { method, url: redactUrl(url), status: response.status, duration_ms: Math.round(performance.now() - started), via: "fetch" },
          { requestId }
        );
      }
      return response;
    } catch (error) {
      logEvent(
        "http_error",
        {
          method,
          url: redactUrl(url),
          status: null,
          message: String(error?.message || error).slice(0, 300),
          duration_ms: Math.round(performance.now() - started),
          online: navigator.onLine,
          via: "fetch",
        },
        { requestId }
      );
      throw error;
    }
  };
  logged.__vibezLogged = true;
  window.fetch = logged;
}

// ── WebSocket ───────────────────────────────────────────────────────────────────
const sockets = new Map();
let nextSocket = 1;

function messageType(data) {
  if (typeof data !== "string") return { type: "binary" };
  try {
    const parsed = JSON.parse(data);
    return { type: parsed?.type || "unknown", event_id: parsed?.event_id };
  } catch {
    return { type: "text" };
  }
}

function instrumentSocket(ws, rawUrl) {
  const socket = nextSocket++;
  const url = redactUrl(rawUrl);
  const openedAt = Date.now();
  const stats = { url, pings: 0, pongs: 0, lastPongAt: null };
  sockets.set(socket, stats);
  logEvent("ws_connecting", { socket, url, secure_context: window.isSecureContext });

  ws.addEventListener("open", () => logEvent("ws_open", { socket, url, ms: Date.now() - openedAt }));
  ws.addEventListener("error", () => logEvent("ws_error", { socket, url, ready_state: ws.readyState, online: navigator.onLine }));
  ws.addEventListener("close", event => {
    sockets.delete(socket);
    logEvent("ws_close", {
      socket,
      url,
      code: event.code,
      reason: String(event.reason || "").slice(0, 200) || undefined,
      clean: event.wasClean,
      lifetime_ms: Date.now() - openedAt,
      pings: stats.pings,
      pongs: stats.pongs,
    });
  });
  ws.addEventListener("message", event => {
    const { type, event_id } = messageType(event.data);
    if (type === "pong") {
      stats.pongs += 1;
      stats.lastPongAt = Date.now();
      return;
    }
    logEvent("ws_message", {
      socket,
      type,
      event_id,
      bytes: typeof event.data === "string" ? event.data.length : undefined,
    });
  });

  const send = ws.send.bind(ws);
  ws.send = data => {
    try {
      const { type } = messageType(data);
      if (type === "ping") stats.pings += 1;
      else logEvent("ws_send", { socket, type, ready_state: ws.readyState });
    } catch {
      /* the frame still goes out */
    }
    return send(data);
  };
}

/**
 * A drop-in WebSocket constructor that logs. A factory, not a subclass: CRA's Babel
 * can transpile classes in a way that breaks extending a native constructor, and a
 * failure here would take realtime down with it. `new` on a function that returns an
 * object yields that object, so callers get the genuine native socket; the shared
 * prototype keeps `instanceof WebSocket` true.
 */
export function createLoggedWebSocket(Native) {
  function LoggedWebSocket(url, protocols) {
    const ws = protocols === undefined ? new Native(url) : new Native(url, protocols);
    try {
      instrumentSocket(ws, url);
    } catch {
      /* an un-instrumented socket is still a working socket */
    }
    return ws;
  }
  LoggedWebSocket.prototype = Native.prototype;
  ["CONNECTING", "OPEN", "CLOSING", "CLOSED"].forEach(name => {
    LoggedWebSocket[name] = Native[name];
  });
  LoggedWebSocket.__vibezLogged = true;
  LoggedWebSocket.__native = Native;
  return LoggedWebSocket;
}

function installWebSocket() {
  const Native = window.WebSocket;
  if (typeof Native !== "function" || Native.__vibezLogged) return;
  window.WebSocket = createLoggedWebSocket(Native);

  setInterval(() => {
    sockets.forEach((stats, socket) => {
      if (!stats.pings && !stats.pongs) return;
      logEvent("ws_heartbeat", {
        socket,
        url: stats.url,
        pings: stats.pings,
        pongs: stats.pongs,
        last_pong_ago_ms: stats.lastPongAt ? Date.now() - stats.lastPongAt : null,
      });
      stats.pings = 0;
      stats.pongs = 0;
    });
  }, HEARTBEAT_SUMMARY_MS);
}

export function installNetworkCapture() {
  if (installed || typeof window === "undefined") return;
  installed = true;
  instrumentAxios(axios);
  installFetch();
  installWebSocket();
}
